// Order seems to affect how the CSS behaves. Let's ignore sorting in this file for now

import "@/sentry";
import { useEffect, useState } from "react";
import { matchPath } from "react-router";

import { Redirect, Route, Switch, useLocation } from "react-router-dom";

import { useUser } from "@/hooks/useUser";

import { CreateAlertView } from "@/views/CreateAlertView";
import MentionsView from "@/views/MentionsView";

// ----------------------------------------------------

import {
  AMPLITUDE_PROJECT_TOKEN,
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
} from "@/config";
import AlertProvider from "@/containers/AlertProvider";
import I18nProvider from "@/containers/I18nProvider";
import TagProvider from "@/containers/TagProvider";
import { useDelighted } from "@/hooks/useDelighted";
import { useIntercom } from "@/hooks/useIntercom";
import usePreferredTheme from "@/hooks/usePreferredTheme";
import { ROUTES } from "@/routes_new";
import { User } from "@/types/User";
import { isAllEarsEmail, isDTDGEmail } from "@/utils/email";
import ArchiveSearch from "@/views/ArchiveSearch";
import ArchiveView from "@/views/archive";
import SavedSearchView from "@/views/saved-searches";
import TagView from "@/views/tags";
import * as amplitude from "@amplitude/session-replay-browser";
import RegisterChartLocale from "@components/Charts/RegisterChartLocale";
import { setupChartTheme } from "@components/Charts/base";
import { datadogRum } from "@datadog/browser-rum";
import { MentionsRedirectView } from "../MentionsRedirectView";
import { OrgRedirectView } from "../OrgRedirectView";
import { ReportsPublicDetailView } from "../reports/ReportsPublicDetailView";
import HomeView from "../home";
import OverviewView from "../reports/OverviewView";
import { ReportBuilder } from "../reports/ReportBuilder";
import { Feature } from "@components/Feature";
import { FEATURE } from "@/features";
import NotAllowed from "@/containers/StatusPages/NotAllowed";
import SettingsLayout from "../settings/SettingsLayout";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import SettingsAccountView from "../settings/partials/account";
import SettingsEmailView from "../settings/partials/email";
import SettingsUsersView from "../settings/partials/users";
import SettingsApiView from "../settings/partials/api";
import WelcomeNew from "./WelcomeNew";

const isProduction = process.env.NODE_ENV === "production";

if (isProduction) {
  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: "datadoghq.eu",
    service: "frontend",
    env: "env:production",
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
  });
}

const SystemTheme = () => {
  const systemDarkMode = usePreferredTheme("dark");

  useEffect(() => {
    document.body.classList.toggle("dark", systemDarkMode);
    setupChartTheme();
  }, [systemDarkMode]);
  return null;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Theme = () => {
  const { data: user } = useUser();
  const theme = (user?.data as User)?.theme;

  useEffect(() => {
    setupChartTheme();
  }, [theme]);

  if (theme === "dark") {
    document.body.classList.add("dark");
  } else if (theme === "system") {
    return <SystemTheme />;
  } else {
    document.body.classList.remove("dark");
  }
  return null;
};

const AppRoot = () => {
  useDelighted();
  useIntercom();

  const { pathname } = useLocation();
  const { data: user } = useUser();
  const [isAmplitudeSetUp, setIsAmplitudeSetUp] = useState<boolean>(false);

  useEffect(() => {
    if (isProduction && !isAmplitudeSetUp && user?.ok) {
      const userEmail = user.data.email;

      if (!isAllEarsEmail(userEmail) && !isDTDGEmail(userEmail)) {
        amplitude.init(AMPLITUDE_PROJECT_TOKEN, {
          sampleRate: 0.4,
        });

        setIsAmplitudeSetUp(true);
      }
    }
  }, [user, isAmplitudeSetUp]);

  const isPublic = Boolean(
    matchPath(pathname, { path: ROUTES.REPORT_PUBLIC }) ||
    matchPath(pathname, { path: ROUTES.INSIGHTS_PUBLIC })
  );

  if (!isPublic && user?.status === 401) {
    return <Redirect to={ROUTES.LOGIN} />;
  }

  return (
    <>
      <I18nProvider>
        <TagProvider>
          <AlertProvider>
            <Switch>
              <Route
                exact
                path={[ROUTES.REPORT_PUBLIC, ROUTES.INSIGHTS_PUBLIC]}
              >
                <ReportsPublicDetailView />
              </Route>

              <Route exact path={ROUTES.APP_HOME} component={OrgRedirectView} />
              <Route exact path={ROUTES.DASHBOARD} component={HomeView} />
              <Route
                exact
                path={ROUTES.MENTION_LIST}
                component={OrgRedirectView}
              />
              <Route
                exact
                path={ROUTES.MENTION_LIST_ORGID}
                component={MentionsRedirectView}
              />
              <Route path={ROUTES.SEARCH} component={OrgRedirectView} />
              <Route path={ROUTES.SEARCH_ORGID} component={ArchiveSearch} />
              <Route
                exact
                path={ROUTES.MENTION_NEW}
                component={OrgRedirectView}
              />
              <Route
                exact
                path={ROUTES.MENTION_NEW_ORGID}
                component={CreateAlertView}
              />
              <Route path={ROUTES.TAGS} component={OrgRedirectView} />
              <Route path={ROUTES.TAGS_ORGID} component={TagView} />
              <Route path={ROUTES.FAVORITES_ORGID} component={TagView} />
              <Route path={ROUTES.FAVORITES} component={OrgRedirectView} />
              <Route
                path={ROUTES.MENTIONS_ANCHOR}
                component={OrgRedirectView}
              />
              <Route path={ROUTES.MENTION_DETAIL} component={OrgRedirectView} />
              <Route
                path={ROUTES.MENTION_DETAIL_ORGID}
                component={MentionsView}
              />
              <Route path={ROUTES.ARCHIVE} component={OrgRedirectView} />
              <Route path={ROUTES.ARCHIVE_ORGID} component={ArchiveView} />
              <Route
                path={ROUTES.SAVED_SEARCHES_ORGID}
                component={SavedSearchView}
              />
              <Route path={ROUTES.SAVED_SEARCHES} component={OrgRedirectView} />
              <Route path={ROUTES.REPORTS} component={OrgRedirectView} />
              <Route path={ROUTES.SETTINGS} component={OrgRedirectView} />
              <Route
                path={ROUTES.SETTINGS_ORGID}
                render={(props) => (
                  <SettingsLayout {...props}>
                    <Switch>
                      <Route
                        exact
                        path={[
                          ROUTES.SETTINGS_ORGID,
                          ROUTES.SETTINGS_CHANGE_PASSWORD_ORGID,
                        ]}
                        render={() => (
                          <AccessControl
                            level={ACCESS_LEVELS.ADMIN}
                            fallback={<NotAllowed />}
                          >
                            <SettingsAccountView />
                          </AccessControl>
                        )}
                      />

                      <Route
                        path={ROUTES.SETTINGS_EMAIL_ORGID}
                        component={SettingsEmailView}
                      />

                      <Route
                        path={ROUTES.SETTINGS_TEAM_ORGID}
                        render={() => (
                          <AccessControl level={ACCESS_LEVELS.ADMIN}>
                            <SettingsUsersView />
                          </AccessControl>
                        )}
                      />

                      <Route
                        path={ROUTES.SETTINGS_API_ORGID}
                        render={() => (
                          <Feature
                            required={[
                              FEATURE.HAS_API_ACCESS,
                              FEATURE.HAS_SEARCH_API_ACCESS,
                            ]}
                            fallback={<NotAllowed />}
                          >
                            <SettingsApiView />
                          </Feature>
                        )}
                      />
                    </Switch>
                  </SettingsLayout>
                )}
              />
              <Route path={ROUTES.INVITE_USER} component={OrgRedirectView} />
              <Route path={ROUTES.WELCOME} component={WelcomeNew} />

              <Route
                exact
                path={ROUTES.APP_HOME_ORGID}
                component={MentionsRedirectView}
              />

              <Route
                exact
                path={ROUTES.REPORTS_ORGID}
                render={(props) => (
                  <Feature
                    required={FEATURE.HAS_REPORT_PRODUCT}
                    fallback={<NotAllowed />}
                  >
                    <OverviewView {...props} />
                  </Feature>
                )}
              />
              <Route
                exact
                path={ROUTES.REPORTS_NEW_ORGID}
                render={(props) => (
                  <Feature
                    required={FEATURE.HAS_REPORT_PRODUCT}
                    fallback={<NotAllowed />}
                  >
                    <ReportBuilder {...props} isNewReport={true} />
                  </Feature>
                )}
              />
              <Route
                exact
                path={ROUTES.REPORTS_EDIT_ORGID}
                render={(props) => (
                  <Feature
                    required={FEATURE.HAS_REPORT_PRODUCT}
                    fallback={<NotAllowed />}
                  >
                    <ReportBuilder {...props} />
                  </Feature>
                )}
              />
              <Route
                exact
                path={ROUTES.REPORTS_EDIT_SUBSCRIPTIONS_ORGID}
                render={(props) => (
                  <Feature
                    required={FEATURE.HAS_REPORT_PRODUCT}
                    fallback={<NotAllowed />}
                  >
                    <ReportBuilder {...props} showScheduleModalOnInit={true} />
                  </Feature>
                )}
              />
            </Switch>
          </AlertProvider>
        </TagProvider>
        <RegisterChartLocale />
      </I18nProvider>
      {/* <Theme /> */}
    </>
  );
};

export default AppRoot;
